import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { SceneLoader } from '@babylonjs/core/Loading/sceneLoader';
import { HDRCubeTexture } from '@babylonjs/core/Materials/Textures/hdrCubeTexture';
import '@babylonjs/core/Loading/Plugins/babylonFileLoader';
// Babylon 9 no longer registers these as side effects of other modules. Without
// instancedMesh, .babylon files that contain instances fail to load; without ray,
// scene.pick() is a stub that never hits, so the rotation gizmo rings and the
// click-to-select planes in the model editor stop responding (DES-175).
import '@babylonjs/core/Meshes/instancedMesh';
import '@babylonjs/core/Culling/ray';
import '@babylonjs/core/Materials/PBR/pbrMaterial';
import '@babylonjs/loaders/glTF';

// Same lighting as the models inspector / editor: HDR environment + a soft
// hemispheric fill and a directional key light.
const setupLighting = (scene) => {
    scene.clearColor = new Color4(0.08, 0.08, 0.1, 1);
    try {
        scene.environmentTexture = new HDRCubeTexture(`${process.env.PUBLIC_URL}/inspector/room.hdr`, scene, 256);
    } catch {
        /* env is optional — lights below still illuminate the model */
    }
    const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), scene);
    hemi.intensity = 0.7;
    hemi.diffuse = new Color3(1, 0.98, 0.957);
    const dir = new DirectionalLight('dir', new Vector3(0.5, -1, 0.5), scene);
    dir.intensity = 0.5;
    dir.diffuse = new Color3(1, 0.98, 0.957);
};

/** Engine + scene + an attached ArcRotate camera, ready to render. */
export const setupScene = (canvas) => {
    const engine = new Engine(canvas, true, { preserveDrawingBuffer: true });
    const scene = new Scene(engine);
    setupLighting(scene);

    const camera = new ArcRotateCamera('cam', -Math.PI / 4, Math.PI / 3, 4, Vector3.Zero(), scene);
    camera.attachControl(canvas, true);
    camera.wheelPrecision = 40;
    camera.minZ = 0.01;
    camera.lowerRadiusLimit = 0.2;

    engine.runRenderLoop(() => {
        if (!scene.isDisposed) scene.render();
    });
    return { engine, scene, camera };
};

/** Aims the camera at the meshes' combined bounding box. */
export const frameCamera = (camera, meshes) => {
    const bounds = worldBounds(meshes);
    if (!bounds) return;
    const { min, max } = bounds;
    const size = max.subtract(min);
    camera.setTarget(min.add(max).scale(0.5));
    camera.radius = Math.max(size.x, size.y, size.z) * 2.2 || 4;
};

/** Combined world-space {min, max} of the meshes that have geometry, or null. */
export const worldBounds = (meshes) => {
    let min = null;
    let max = null;
    meshes.forEach((m) => {
        if (!m.getTotalVertices || m.getTotalVertices() === 0) return;
        m.computeWorldMatrix(true);
        const bb = m.getBoundingInfo()?.boundingBox;
        if (!bb) return;
        min = min ? Vector3.Minimize(min, bb.minimumWorld) : bb.minimumWorld.clone();
        max = max ? Vector3.Maximize(max, bb.maximumWorld) : bb.maximumWorld.clone();
    });
    return min ? { min, max } : null;
};

/** Loads a .glb/.babylon blob into the scene; returns the SceneLoader result. */
export const loadModelFromBlob = async (scene, blob, ext = '.glb') => {
    const objectUrl = URL.createObjectURL(blob);
    try {
        return await SceneLoader.ImportMeshAsync('', '', objectUrl, scene, null, ext);
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
};
